import { getBlackBoxValues } from 'syf-js-utilities'
import type { AnyObject } from 'syf-component-library/typings/utils'
import { getSessionID } from 'helpers/authUtils'
import getCorrelationId from 'helpers/getCorrelationId'
import getApiHostforAuthService from 'helpers/getApiHostForAuthService'
import fetchBasic from 'services/helpers/fetchBasic'
import type { FetchBasicConfig } from 'services/helpers/fetchBasic'
import { HTTPDataError } from 'services/helpers/HTTPDataError'
import {
  AuthenticateMutateProps,
  AuthenticateMutationData,
  AuthenticateMutationOption,
  AuthenticateUseMutationProps
} from 'services/authenticateMutation/authenticateMutation.types'
import handleAuthenticateErrorCases from 'services/authenticateMutation/handleAuthenticateErrorCases'
import { authenticateURL } from 'services/authenticateMutation/authenticateMutation.constants'
import getTransId from 'helpers/getTransId'
import { setNewRelicPageAction } from 'helpers/newRelic'

const makeSecuredAuthenticateOptions = ({
  config,
  mutationOptions
}: AuthenticateUseMutationProps): AuthenticateMutationOption => {
  const { APIGEE_CLIENT_ID } = config
  // override to use an actual auth server on local instead of sandbox for realistic dev
  const apiHostToUse = getApiHostforAuthService(config)

  return {
    // 1 key since each login overwrites the previous fetch
    mutationKey: [authenticateURL],
    mutationFn: async ({
      ssn,
      accountNumber,
      zipCode,
      flowId = 'secured-login'
    }: AuthenticateMutateProps) => {
      const headers: HeadersInit = {
        'X-SYF-Client-Id': APIGEE_CLIENT_ID,
        // randomly generated hex value for tracking each request
        'X-SYF-Request-TrackingId': getTransId(),
        // the interaction id from /introspect
        'X-SYF-Auth-Session-Id': getSessionID(),
        'X-SYF-Request-CorrelationId': getCorrelationId()
      }
      // device fingerprint for antifraud, have to await bc its async generation
      const blackboxValues = await getBlackBoxValues()
      const body: BodyInit = JSON.stringify({
        deviceInfo: {
          // the string value for the fingerprint
          blackBox: blackboxValues?.blackbox
        },
        // user login input
        userInfo: {
          'cipher.lastFourSSN': ssn,
          accountNumber,
          zipCode
        },
        flowId
      })

      const requestConfig: RequestInit = {
        method: 'POST',
        headers,
        body
      }

      const fetchOptions: FetchBasicConfig = {
        baseURL: apiHostToUse,
        init: requestConfig
      }
      // log authenticate start with headers for better debugging user flow
      setNewRelicPageAction(
        `Start ${requestConfig.method} ${authenticateURL}`,
        headers
      )
      // fetchBasic because this is an auth call so no auth headers available yet
      return fetchBasic<AuthenticateMutationData>(authenticateURL, fetchOptions)
    },
    // don't trigger error boundary for certain status/response combos
    useErrorBoundary: (error: HTTPDataError<AnyObject>) => {
      // always use the error boundary for any non http response errors
      if (!error.response) {
        return true
      }
      return handleAuthenticateErrorCases(error)
    },
    ...mutationOptions
  }
}

export default makeSecuredAuthenticateOptions
